import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React from 'react';
import PropTypes from 'prop-types';
import _identity from 'lodash/identity';
import getDisplayName from 'react-display-name';
export var createOnChangeWithTransform = function createOnChangeWithTransform(dispatchChange, onChange) {
  var onChangeTransform = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : _identity;
  var parser = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : _identity;
  var onValidationSuccess = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : _identity;
  var onValidationFail = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : _identity;
  return function (e) {
    var result = dispatchChange(parser(onChangeTransform(e))).then(onValidationSuccess, onValidationFail);
    onChange && onChange(e);
    return result;
  };
};
/**
 * Higher order component that wraps a given input to be compatible with redux-inputs
 *
 * The wrapped component will be given the following props:
 * - _id - An identifier unique to the input consisting of mount point and input id
 * - value - The current displayed value
 * - error - True if current value is invalid
 * - dispatchChange - callback for updating the value
 * - errorText - String to display to the user of current error
 */

var ReduxInputsWrapper = function ReduxInputsWrapper(WrappedComponent) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    onChangeTransform: _identity
  };

  var Wrapper = function Wrapper(_ref) {
    var id = _ref.id,
        _id = _ref._id,
        value = _ref.value,
        parser = _ref.parser,
        formatter = _ref.formatter,
        dispatchChange = _ref.dispatchChange,
        onChange = _ref.onChange,
        onValidationSuccess = _ref.onValidationSuccess,
        onValidationFail = _ref.onValidationFail,
        otherProps = _objectWithoutProperties(_ref, ["id", "_id", "value", "parser", "formatter", "dispatchChange", "onChange", "onValidationSuccess", "onValidationFail"]);

    var onChangeTransform = options.onChangeTransform;
    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({
      id: id || _id,
      value: formatter ? formatter(value) : value // onChange function to take an event facade, dispatch a change event
      ,
      onChange: createOnChangeWithTransform(dispatchChange, onChange, onChangeTransform, parser, onValidationSuccess, onValidationFail)
    }, otherProps));
  };

  Wrapper.displayName = "ReduxInputsWrapper(".concat(getDisplayName(WrappedComponent), ")");
  Wrapper.propTypes = {
    /**
     * Generated id from getInputProps (mountPoint:key)
     */
    _id: PropTypes.string.isRequired,

    /**
     * Optional override id if you don't want to use default _id from inputConfig key
     */
    id: PropTypes.string,

    /**
     * The logical value of the input from the redux store
     */
    value: PropTypes.any,

    /**
     * Function to create a value from a change event into a value to store
     */
    parser: PropTypes.func,

    /**
     * Function to create a DOM-appropriate string from a value
     */
    formatter: PropTypes.func,

    /**
     * Private function from getInputProps, returns a promise
     */
    dispatchChange: PropTypes.func.isRequired,

    /**
     * Callback for React's native onChange
     */
    onChange: PropTypes.func,

    /**
     * Callback after successful validation and update
     */
    onValidationSuccess: PropTypes.func,

    /**
     * Callback after failed validation
     */
    onValidationFail: PropTypes.func
  };
  return Wrapper;
};

export default ReduxInputsWrapper;